<template>
    <div v-if="isStripePayment">
        <Teleport to="#checkoutNavigation">
            <nav class="navigation flex flex-col flex-wrap justify-between items-center gap-2 lg:flex-row lg:flex-nowrap lg:items-center">
                <div
                    class="btn btn-lg btn-tertiary w-full order-2 lg:order-1 lg:w-auto lg:btn-sm"
                    role="button"
                    tabindex="0"
                    @click="selectStep('payment')"
                    @keyup.enter="selectStep('payment')"
                >
                    <MiscIcon name="chevron-left" />
                    {{ $t('checkout.summary.navigationBack') }}
                </div>
                <div class="w-full flex flex-col items-end gap-2 order-1 lg:w-auto lg:order-2">
                    <button
                        type="submit"
                        :disabled="loading || !isElementReady"
                        class="btn btn-lg btn-accent w-full lg:w-auto"
                        data-cy="checkout-navigation-place-order"
                        @click.prevent="onSubmit()"
                    >
                        <span v-if="loading" class="loading" />
                        <MiscIcon v-else name="check" />
                        {{ $t('checkout.summary.navigationPlaceOrder') }}
                    </button>
                    <div v-if="!isElementReady && !loading" class="bg-error p-2 text-center text-error-content text-xs">
                        {{ $t('checkout.stripe.configurePaymentFirst') }}
                        <button
                            class="cursor-pointer underline"
                            @click="selectStep('payment')"
                            @keyup.enter="selectStep('payment')"
                        >
                            {{ $t('checkout.stripe.paymentStep') }}
                        </button>.
                    </div>
                    <div class="gap-1 items-center mt-3 text-base-content text-xs font-normal flex lg:hidden">
                        <MiscIcon name="lock" size="xs" />
                        {{ $t('cart.transaction') }}
                        <div class="font-bold underline lowercase">
                            {{ $t('cart.safe') }}
                        </div>
                    </div>
                </div>
            </nav>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
import { formValidation } from '@/utils/formValidation'

const props = defineProps<{
    form: HTMLFormElement | null
    customerComment?: string
    newsletter?: boolean
}>()

const emit = defineEmits(['update:defaultPlaceOrder', 'update:selectStep', 'onError', 'orderPlaced'])

const { validateForm } = formValidation()
const { createOrder } = useCheckout()
const { handlePayment } = usePayment()
const { showNotification } = useNotification()
const { sessionContext, selectedPaymentMethod } = useSessionContext()
const { user } = useUser()
const { cart, refreshCart } = useCart()
const { stripe, elements, configs, options, isElementReady } = useStripe()
const { getSalutationId, subscribeToNewsletter } = useNewsletterSubscription()

const isStripePayment = ref(false)
const loading = ref(false)
const showOverlay = useState<boolean>('checkout-overlay', () => false)

onMounted(() => {
    if (selectedPaymentMethod.value?.shortName === 'stripe_payments_app_stripe_payment') {
        emit('update:defaultPlaceOrder', false)
        isStripePayment.value = true
    }
})

async function onSubmit () {
    if (!props.form) return
    const isValid = await validateForm(props.form)
    if (!isValid) return

    // Validate stripe elements
    if (elements?.value != null && configs?.value?.paymentElements != null) {
        const { error: submitError } = await elements.value.submit()

        if (submitError) {
            console.error(submitError)
            selectStep('payment')
            document?.querySelector('#stripe-payment-element')?.scrollIntoView()
            return
        }
    } else {
        selectStep('payment')
        document?.querySelector('#stripe-payment-element')?.scrollIntoView()
        return
    }

    if (configs?.value?.paymentElements) {
        try {
            loading.value = true
            showOverlay.value = true

            // Create billing details via Stripe connector app
            const createBillingDetails = await fetch((options.value.appUrl + '/create-billing-details'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    shopId: options.value.appShopId,
                    countryIso: sessionContext.value?.shippingLocation?.country?.iso,
                    countryName: sessionContext.value?.shippingLocation?.country?.name,
                    firstName: user.value?.activeShippingAddress?.firstName,
                    lastName: user.value?.activeShippingAddress?.lastName,
                    zipCode: user.value?.activeShippingAddress?.zipcode,
                    city: user.value?.activeShippingAddress?.city,
                    street: user.value?.activeShippingAddress?.street,
                    email: user.value?.email,
                    appVersion: options.value.appVersion
                }),
            }).then((r) => r.json())

            // Create payment method via Stripe JS
            const pm = await stripe?.value?.createPaymentMethod({
                elements: elements?.value,
                params: createBillingDetails.cardPayment.payment_method
            })

            if (pm?.paymentMethod?.id) {
                await fetch((options.value.appUrl + '/create-intent'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        shopId: options.value.appShopId,
                        salesChannelId: sessionContext.value?.salesChannel?.id,
                        countryIso: sessionContext.value?.shippingLocation?.country?.iso,
                        countryName: sessionContext.value?.shippingLocation?.country?.name,
                        firstName: user.value?.activeShippingAddress?.firstName,
                        lastName: user.value?.activeShippingAddress?.lastName,
                        zipCode: user.value?.activeShippingAddress?.zipcode,
                        city: user.value?.activeShippingAddress?.city,
                        street: user.value?.activeShippingAddress?.street,
                        email: user.value?.email,
                        currencyIso: sessionContext.value?.currency?.isoCode,
                        amount: cart.value?.price?.totalPrice,
                        paymentMethodId: selectedPaymentMethod.value?.id,
                        customerId: user.value?.id,
                        stripePaymentMethodId: pm.paymentMethod.id,
                        transactionId: null, // TODO: handle orderId if on payments error page
                        appVersion: options.value.appVersion
                    }),
                }).then((r) => r.json())
            }

            const order = await createOrder({ customerComment: props.customerComment })
            const { redirectUrl } = await handlePayment(order.id)

            // Subscribe to newsletter if checkbox was checked
            if (props.newsletter && user.value) {
                try {
                    const salutationId = getSalutationId(user.value)
                    if (salutationId) {
                        await subscribeToNewsletter(
                            user.value.email,
                            salutationId,
                            user.value.firstName,
                            user.value.lastName
                        )
                    }
                } catch (error) {
                    // Don't fail the order if newsletter subscription fails
                    console.error('Newsletter subscription failed:', error)
                }
            }

            if (redirectUrl) {
                emit('orderPlaced', order.id)
                window.location.href = redirectUrl
            } else {
                await refreshCart()
            }
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : String(e)
            console.error(e)
            showNotification(message, 'error', true)

            if (message.includes('payment-method-blocked')) {
                emit('onError', 'payment-method-blocked')
            }

            loading.value = false
            showOverlay.value = false
        }
    }
}

function selectStep (step: string) {
    emit('update:selectStep', step)
}
</script>